/**
 * Charge types.
 *
 * The app recognises six statutory charge categories ("slugs"). Each has a
 * profile in the CHARGES database that drives juror scoring, flags, and the
 * research/analysis content.
 */

export type ChargeSlug = 'murder' | 'drug' | 'fraud' | 'assault' | 'oui' | 'sexual';

export const CHARGE_SLUGS: readonly ChargeSlug[] = [
  'murder',
  'drug',
  'fraud',
  'assault',
  'oui',
  'sexual'
];

/**
 * Weighted juror signals. Each charge specifies a partial subset; an absent
 * key contributes 0. Positive/negative values are read from the defense or
 * prosecution perspective by the scoring engine.
 */
export interface ScoringWeights {
  crimeVictim?: number;
  lawEnforcement?: number;
  highEdu?: number;
  youngAge?: number;
  blueCollarOcc?: number;
  businessOcc?: number;
  therapist?: number;
  // NOTE: femaleGender / youngMale were removed — a juror's own sex is never a
  // scoring factor (protected-class "Lane 2"; Soares broader than Batson).
}

/** A single scoring-weight signal name. */
export type WeightKey = keyof ScoringWeights;

/** An entry in the CHARGES database, keyed by {@link ChargeSlug}. */
export interface ChargeProfile {
  label: string;
  icon: string;
  defWeights: ScoringWeights;
  prosWeights: ScoringWeights;
  defFlags: string[];
  prosFlags: string[];
  notes: string;
  defResearch: string[];
  prosResearch: string[];
  defAvoidTraits: string[];
  prosAvoidTraits: string[];
}

/**
 * A free-text charge the user typed into the Case tab's charge list.
 *
 * NOTE: in the original monolith these objects and the {@link ChargeSlug}
 * strings were both pushed onto a single `state.activeCharges` array, which
 * caused the two representations to clobber each other (and could crash the
 * scorer via `CHARGES[{id,text}]`). The rewrite keeps them in separate state
 * fields — see AppState.chargeList vs AppState.activeCharges.
 */
export interface ChargeDescription {
  id: number;
  text: string;
}
